import type { CSSProperties } from 'react';
import {
    FileAttachmentIcon,
    type ImageLightboxItem,
    fileKindLabel,
    isImageAttachment,
} from './AttachmentPreview.tsx';
import type { StoredCreateTaskAttachment } from '../utils/createTaskStorage';

export interface CreateTaskAttachmentListProps {
    attachments: StoredCreateTaskAttachment[];
    onRemoveAttachment?: (clientID: string) => void;
    onOpenImage?: (item: ImageLightboxItem) => void;
}

type CreateTaskAttachmentTileProps = {
    attachment: StoredCreateTaskAttachment;
    onRemove?: () => void;
    onOpenImage?: (item: ImageLightboxItem) => void;
};

const tileStatusLabels: Record<StoredCreateTaskAttachment['status'], string> = {
    pending: 'Pending',
    too_many_attachments: 'Too many attachments',
    too_large: 'File too large',
    creating: 'Preparing upload',
    uploading: 'Uploading',
    scanning: 'Scanning',
    ready: 'Ready',
    error: 'Attachment failed',
};

function FileTileIcon() {
    return (
        <div className="create-task-attachment-tile__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                <path d="M7 3.75h7l5 5v11.5a1.75 1.75 0 0 1-1.75 1.75h-10.5A1.75 1.75 0 0 1 5 20.25V5.5A1.75 1.75 0 0 1 6.75 3.75Z" />
                <path d="M14 3.75v5h5" />
            </svg>
        </div>
    );
}

function ErrorTileIcon() {
    return (
        <div className="create-task-attachment-tile__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
                <path
                    d="M12 3.5 2.8 19.5a1 1 0 0 0 .87 1.5h16.66a1 1 0 0 0 .87-1.5L12 3.5Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                />
                <path d="M12 9v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="17" r="1.25" fill="currentColor" />
            </svg>
        </div>
    );
}

function InlineSpinner() {
    return <div className="create-task-attachment-tile__spinner" aria-hidden="true" />;
}

function progressBarStyle(progress: number): CSSProperties {
    return {
        width: `${Math.max(0, Math.min(100, progress))}%`,
    };
}

export function CreateTaskAttachmentTile({ attachment, onRemove, onOpenImage }: CreateTaskAttachmentTileProps) {
    const readyFileID = attachment.status === 'ready' ? attachment.fileID : undefined;
    const isReady = typeof readyFileID === 'string';
    const readyHref = isReady ? `/files/${encodeURIComponent(readyFileID)}` : undefined;
    const isReadyImage = isReady && typeof attachment.contentType === 'string' && isImageAttachment(attachment.contentType);
    const isReadyNonImage = isReady && typeof attachment.contentType === 'string' && !isImageAttachment(attachment.contentType);
    const isErrorTile = attachment.status === 'error' || attachment.status === 'too_large' || attachment.status === 'too_many_attachments';
    const showImageThumbnail = isReadyImage;
    const detailText = (() => {
        switch (attachment.status) {
            case 'pending':
            case 'creating':
            case 'scanning':
                return tileStatusLabels[attachment.status];
            case 'uploading':
                return `${tileStatusLabels.uploading} ${Math.round(attachment.progress)}%`;
            case 'ready':
                return attachment.contentType ?? tileStatusLabels.ready;
            case 'too_large':
            case 'too_many_attachments':
                return attachment.error ?? tileStatusLabels[attachment.status];
            case 'error':
                return attachment.error ?? tileStatusLabels.error;
            default:
                return '';
        }
    })();

    return (
        <div className={`create-task-attachment-tile create-task-attachment-tile--${attachment.status}`}>
            {onRemove && (
                <button
                    type="button"
                    className="create-task-attachment-tile__remove btn-icon"
                    aria-label={`Remove attachment ${attachment.name}`}
                    onClick={onRemove}
                >
                    x
                </button>
            )}
            {showImageThumbnail ? (
                <button
                    type="button"
                    className="create-task-attachment-tile__thumbnail-button"
                    aria-label={`Open image attachment ${attachment.name}`}
                    onClick={() => {
                        if (readyHref && onOpenImage) {
                            onOpenImage({
                                fileName: attachment.name,
                                downloadUrl: readyHref,
                            });
                        }
                    }}
                >
                    <img
                        src={readyHref}
                        alt={attachment.name}
                        className="create-task-attachment-tile__thumbnail"
                        onError={(event) => {
                            const target = event.currentTarget;
                            target.style.display = 'none';
                            const fallback = target.parentElement?.nextElementSibling;
                            if (fallback instanceof HTMLElement) {
                                fallback.hidden = false;
                            }
                        }}
                    />
                </button>
            ) : null}
            <div
                className="create-task-attachment-tile__icon create-task-attachment-tile__icon--file-kind"
                hidden={showImageThumbnail || !isReadyNonImage || !attachment.contentType}
            >
                {isReadyNonImage && attachment.contentType ? (
                    <FileAttachmentIcon label={fileKindLabel(attachment.contentType)} />
                ) : null}
            </div>
            {!showImageThumbnail && !isReadyNonImage && (isErrorTile ? <ErrorTileIcon /> : <FileTileIcon />)}
            <div className="create-task-attachment-tile__body">
                {isReady ? (
                    <a
                        href={readyHref}
                        className="create-task-attachment-tile__name attachment-file-link"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {attachment.name}
                    </a>
                ) : (
                    <div className="create-task-attachment-tile__name">{attachment.name}</div>
                )}
                <div className="create-task-attachment-tile__meta-row">
                    {attachment.status === 'scanning' && <InlineSpinner />}
                    <div className="create-task-attachment-tile__meta">{detailText}</div>
                </div>
                {attachment.status === 'uploading' && (
                    <div className="create-task-attachment-tile__progress" aria-label={`Upload progress ${Math.round(attachment.progress)}%`}>
                        <div className="create-task-attachment-tile__progress-bar" style={progressBarStyle(attachment.progress)} />
                    </div>
                )}
            </div>
        </div>
    );
}

export default function CreateTaskAttachmentList({ attachments, onRemoveAttachment, onOpenImage }: CreateTaskAttachmentListProps) {
    if (attachments.length === 0) {
        return null;
    }

    return (
        <div className="create-task-attachment-list" aria-label="Attachments">
            {attachments.map((attachment) => (
                <CreateTaskAttachmentTile
                    key={attachment.clientID}
                    attachment={attachment}
                    onRemove={onRemoveAttachment ? () => onRemoveAttachment(attachment.clientID) : undefined}
                    onOpenImage={onOpenImage}
                />
            ))}
        </div>
    );
}
